import { Link, NavLink } from "react-router-dom";
import {
  BriefcaseIcon,
  HeartIcon,
  HomeIcon,
  PhoneIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

import { useMeHome } from "hooks/useMeHome";
import { sidebarEntries, type SidebarEntryKind } from "helpers/me-home";
import { useFirebase } from "firebase";
import { useUser } from "context/UserContext";
import { formatPhoneNumber } from "helpers/phone";
import { PHONE } from "constants/at-need";
import CopyrightNotice from "components/CopyrightNotice";
import LegalLinks from "components/LegalLinks";

// @ts-ignore
import LogoWithType from "svg/logo-with-type.svg";

const KIND_ICON: Record<SidebarEntryKind, typeof HomeIcon> = {
  at_need: HeartIcon,
  pre_need: BriefcaseIcon,
};

type NavItem = {
  to: string;
  label: string;
  icon: typeof HomeIcon;
  end?: boolean;
};

function SidebarNavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-start gap-[11px] rounded-md px-3 py-[9px] text-[15px] leading-[22px] text-soil transition-colors hover:bg-oat ${
          isActive ? "bg-oat font-bold" : ""
        }`
      }
    >
      <Icon
        className="mt-0.5 h-[18px] w-[18px] flex-none text-oat-600"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 break-words">{item.label}</span>
    </NavLink>
  );
}

/** The portal's navigation column: wordmark, nav, the call-us card, the
 * signed-in user's identity (email, or phone when there is no email), and the
 * copyright and legal links. Rendered as the fixed desktop sidebar and inside
 * the mobile drawer. `onNavigate` fires on any nav click so the drawer can
 * close itself. */
export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user: firebaseUser } = useFirebase();
  const { user } = useUser();
  const { home } = useMeHome(firebaseUser !== null);
  const entries = home ? sidebarEntries(home) : [];

  const navItems: NavItem[] = [
    { to: "/", label: "Home", icon: HomeIcon, end: true },
    ...entries.map((entry) => ({
      to: entry.to,
      label: entry.label,
      icon: KIND_ICON[entry.kind],
    })),
    { to: "/account", label: "Account", icon: UserIcon },
  ];

  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ");
  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((name) => name![0])
    .join("")
    .toUpperCase();
  const contact =
    user?.email || (user?.phone ? formatPhoneNumber(user.phone) : null);

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 pb-5 pt-6">
        <Link to="/" onClick={onNavigate} aria-label="After home">
          <LogoWithType height="26px" className="text-soil" />
        </Link>
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 px-3 py-1" aria-label="Main">
        {navItems.map((item) => (
          <SidebarNavLink key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="border-t border-oat-200 p-4">
        <div className="flex flex-col gap-2 rounded-xl bg-oat px-4 py-3.5">
          <div className="whitespace-nowrap text-[15px] text-soil">
            <span className="font-serif">Questions? </span>
            <span className="font-bold text-evergreen">Call us</span>
          </div>
          <a
            href={`tel:${PHONE}`}
            className="flex items-center gap-2 text-[15px] font-bold text-soil transition-colors hover:text-evergreen"
          >
            <PhoneIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
            {formatPhoneNumber(PHONE)}
          </a>
        </div>

        {user && (
          <div className="flex items-center gap-2.5 px-1 pb-1 pt-3.5">
            <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-oat text-xs font-bold text-soil">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-bold text-soil">
                {fullName}
              </div>
              {contact && (
                <div className="truncate text-sm text-oat-800" title={contact}>
                  {contact}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-1.5 pl-1 pt-3 text-[11px] text-oat-700">
          <CopyrightNotice
            showRelease={false}
            className="min-w-0 truncate text-[11px]"
          />
          <LegalLinks className="flex shrink-0 gap-1.5" />
        </div>
      </div>
    </div>
  );
}
